import { useState, memo, type ReactNode, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/context/AuthProvider";
import Popup from "src/components/Popup/Popup";
import { MdOutlineMail, MdPhone } from "react-icons/md";
import { FiTruck } from "react-icons/fi";
import { PiHandArrowDown } from "react-icons/pi";
import { FaRegFolderOpen } from "react-icons/fa6";
import { RiDeleteBin2Line } from "react-icons/ri";
import { TbLink, TbRefresh, TbAlertTriangle, TbMessageQuestion, TbGavel, TbMailOff, TbShieldExclamation, TbCreditCard } from "react-icons/tb";
import CardPaymentButton from "src/components/CardPaymentButton/CardPaymentButton";
import { SlEnvolopeLetter } from "react-icons/sl";
import Link from "src/components/Link/Link";
import InvoiceStatus from "src/components/InvoiceStatus/InvoiceStatus";
import InvoiceOwnerChip from "src/components/InvoiceOwnerChip/InvoiceOwnerChip";
import { Invoice, Tag } from "src/types";
import { InvoiceStatus as IS } from "src/constants/invoice";
import { getAvatarColor, getTagStyle } from "src/helpers/randomColor";
import { invoiceBilledPatientIsLinked } from "src/helpers/invoicePatient";
import { invoiceDisplayName } from "./invoiceListViews";
import { isPostConsentBlocked } from "src/utils/invoiceSendability";
import type { InvoiceNavRow } from "src/helpers/invoiceNavigation";

interface InvoiceRowProps {
    invoice: Invoice;
    selected: boolean;
    onToggle: () => void;
    onSend?: (id: string, readableId: string) => void;
    onSendPost?: (id: string) => void;
    onCheckLetterStatus: (id: string) => void;
    postEnabled: boolean;
    emailEnabled: boolean;
    questionsEnabled?: boolean;
    requireLegalAcceptance?: boolean;
    onDelete: (id: string) => void;
    canDelete?: boolean;
    onPaywise?: (id: string) => void;
    showPaywise?: boolean;
    /** Admin-only, Plus+ feature — both are only ever passed together. */
    showTags?: boolean;
    tagMap?: Map<string, Tag>;
    /** Viewer sees several doctors' invoices — show whose this one is. */
    showOwner?: boolean;
    /** The list's current order (filters, view, sort) — handed to the invoice page for "next invoice". */
    navRows?: InvoiceNavRow[];
    /** Reload after a card payment at the desk — QR (`invoice.cardDeskQr`) or card terminal (`invoice.cardTerminal`). */
    onCardPaid?: () => void;
}

const InvoiceRow = memo(function InvoiceRow({
    invoice,
    selected,
    onToggle,
    onSend,
    onSendPost,
    onCheckLetterStatus,
    postEnabled,
    emailEnabled,
    questionsEnabled = true,
    requireLegalAcceptance = true,
    onDelete,
    canDelete = true,
    onPaywise,
    showPaywise = false,
    showTags = false,
    tagMap,
    showOwner = false,
    navRows,
    onCardPaid,
}: InvoiceRowProps) {
    const routerNavigate = useNavigate();
    // Every way into the invoice from this row carries the list's order along ("next invoice" follows it).
    const navigate = (to: string, options?: { state?: Record<string, unknown> }) =>
        routerNavigate(to, navRows && to.startsWith("/invoices/") ? { ...options, state: { ...options?.state, invoiceRows: navRows } } : options);
    const { t } = useTranslation();
    const { user } = useAuth();
    const isPro = user?.praxis?.entitlements?.goaChecker ?? (user?.praxis?.businessPlan ?? 1) >= 3;
    const [popup, setPopup] = useState<{
        title: string;
        message: ReactNode;
        action: () => void;
        confirmLabel: string;
        confirmVariant: "primary" | "danger";
    } | null>(null);

    const alreadySent = !!(invoice.sentDate && invoice.dueDate);
    const displayName = invoiceDisplayName(invoice);
    const initial = displayName !== "—" ? displayName.charAt(0).toUpperCase() : "?";
    const linked = invoiceBilledPatientIsLinked(invoice);
    const accountName = invoice.recipient
        ? `${invoice.recipient.firstName ?? ""} ${invoice.recipient.lastName ?? ""}`.trim()
        : "";
    const vatRate   = invoice.vatRate ?? 0;
    const net       = (invoice.amount ?? 0) + (invoice.materialCosts ?? 0);
    // Prefer the backend's precomputed per-line aggregate (correct even when exams carry
    // different VAT rates, which `net * vatRate / 100` is not) — only invoices that predate
    // that field fall back to the old flat formula.
    const vatAmount = invoice.vatAmount ?? (vatRate > 0 ? +(net * vatRate / 100).toFixed(2) : 0);
    const vatBreakdown = invoice.vatBreakdown ?? (vatRate > 0 ? [{ rate: vatRate, base: net, vat: vatAmount }] : []);
    // "The whole invoice has VAT" — every line shares the one nonzero rate, nothing exempt
    // mixed in. Only then does the specific "inkl. X% MwSt." badge apply; a genuine mix
    // (some lines taxed, some not, or several rates) gets the generic "inkl. MwSt." badge
    // instead of a misleading single percentage.
    const vatExemptBase = +(net - vatBreakdown.reduce((s, r) => s + r.base, 0)).toFixed(2);
    const vatUniform = vatBreakdown.length === 1 && Math.abs(vatExemptBase) < 0.005;
    const gross     = +(net + vatAmount).toFixed(2);
    const total     = gross + (invoice.dunningFineAmount ?? 0);
    const remaining = Math.max(0, total - (invoice.paidAmount ?? 0));
    // Same disjoint-fields basis as InvoiceAmountsCard.tsx's own overpaid banner — kept in
    // sync so the row-level color and the detail page never disagree.
    const overpaid  = Math.max(0, +((invoice.paidAmount ?? 0) - total).toFixed(2));
    const isPaid = invoice.status === IS.PAID;
    const postOnly = invoice.patient?.postOnly ?? false;
    const legalBlocked = postOnly || (requireLegalAcceptance && !(invoice.questionnaireAccepted ?? false));
    const postBlocked = isPostConsentBlocked(invoice, requireLegalAcceptance);

    return (
        <>
            {popup && (
                <Popup
                    title={popup.title}
                    message={popup.message}
                    actions={[
                        {
                            label: t("popup.cancel"),
                            variant: "default",
                            onClick: () => setPopup(null),
                        },
                        {
                            label: popup.confirmLabel,
                            variant: popup.confirmVariant,
                            onClick: () => {
                                popup.action();
                                setPopup(null);
                            },
                        },
                    ]}
                    onClose={() => setPopup(null)}
                />
            )}
            <tr
                className={`clickable-row${selected ? " row--selected" : ""}${invoice.paywiseCaseId ? " row--paywise" : ""}`}
                onClick={() => navigate(`/invoices/${invoice.id}`)}
            >
                <td className="row-check" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={onToggle}
                    />
                </td>

                <td className="invoice-cell-td" onClick={(e) => e.stopPropagation()}>
                    <div className="invoice-cell">
                        <div
                            className="invoice-avatar"
                            style={{ "--av-bg": getAvatarColor(displayName) } as CSSProperties}
                        >
                            {initial}
                        </div>
                        <div className="invoice-cell__body">
                            <span
                                className="invoice-cell__name invoice-cell__name--link"
                                onClick={() => navigate(`/invoices/${invoice.id}`)}
                            >
                                {invoice.readableId}
                                {invoice.amountMismatch && (
                                    <TbAlertTriangle
                                        className="amount-mismatch-icon"
                                        title={t("invoicePage.amountMismatchShort")}
                                    />
                                )}
                                {isPro && invoice.goaConflict && (
                                    <TbAlertTriangle
                                        className="goa-conflict-row-icon"
                                        title={t("invoicePage.goaConflictRowTooltip", { defaultValue: "Mögliche GOÄ-Abrechnungsfehler in diesem Beleg" })}
                                    />
                                )}
                                {questionsEnabled && invoice.hasQuestion && (
                                    <TbMessageQuestion
                                        className="invoice-question-icon"
                                        title={t("invoicePage.hasQuestion")}
                                    />
                                )}
                                {postOnly && (
                                    <TbMailOff
                                        className="invoice-postonly-icon"
                                        title={t("form.postOnlyHint")}
                                    />
                                )}
                                {!postOnly && legalBlocked && (
                                    <TbShieldExclamation
                                        className="invoice-consent-icon"
                                        title={t("invoiceRow.consentNeededHint")}
                                    />
                                )}
                                {showOwner && <InvoiceOwnerChip invoice={invoice} />}
                            </span>
                            <span className="invoice-cell__sub">
                                {linked && accountName ? (
                                    <>
                                        <Link
                                            link={`/patients/${invoice.recipient?.id}`}
                                            state={{ tab: "details" }}
                                            entities={invoice.recipient ? [invoice.recipient] : []}
                                            text={displayName}
                                        />
                                        {/* Own group so a long patient name pushes the payer onto the
                                            next line instead of clipping it off the end. */}
                                        <span className="invoice-cell__payer">
                                            {" · "}
                                            <PiHandArrowDown className="roster-recipient-icon" aria-hidden />
                                            <strong>
                                                <Link
                                                    link={`/patients/${invoice.recipient?.id}`}
                                                    state={{ tab: "recipient" }}
                                                    entities={invoice.recipient ? [invoice.recipient] : []}
                                                    text={accountName}
                                                />
                                            </strong>
                                        </span>
                                    </>
                                ) : invoice.recipient?.id ? (
                                    <Link
                                        link={`/patients/${invoice.recipient.id}`}
                                        state={invoice.recipientIsPatient ? undefined : { tab: "recipient" }}
                                        entities={invoice.recipient ? [invoice.recipient] : []}
                                        text={displayName}
                                    />
                                ) : (
                                    displayName
                                )}
                                {invoice.recipientIsPatient && (
                                    <span className="invoice-cell__badge">{t("invoicePage.selfPays")}</span>
                                )}
                            </span>
                        </div>
                    </div>
                </td>

                <td className="invoice-status-td">
                    {(invoice.dunning ?? 0) >= 1 && invoice.status !== IS.PAID && invoice.status !== IS.WRITTEN_OFF ? (
                        <span className="dunning-chip-wrap">
                            <span
                                className="dunning-overdue-icon"
                                data-tooltip={invoice.dueDate
                                    ? t("invoices.overdueAt", { date: new Date(invoice.dueDate).toLocaleDateString() })
                                    : t("invoices.overdue")}
                            >
                                <TbAlertTriangle />
                            </span>
                            <span className={`dunning-chip dunning-chip--${invoice.dunning}`}>
                                {invoice.dunning === 4 ? t("filters.dunningLevels.legal") : `M${invoice.dunning}`}
                            </span>
                        </span>
                    ) : (
                        <InvoiceStatus status={invoice.status} unsent={!invoice.sentDate} dunning={invoice.dunning} emailFailed={invoice.emailFailed} />
                    )}
                </td>

                <td className="col-dates">
                    <span className="invoice-dates">
                        {invoice.creationDate && (
                            <span className="invoice-date-pill">
                                {new Date(invoice.creationDate).toLocaleDateString()}
                            </span>
                        )}
                        {invoice.sentDate ? (
                            <span className="invoice-date-pill">
                                {new Date(invoice.sentDate).toLocaleDateString()}
                            </span>
                        ) : (
                            <span className="invoice-date-pill invoice-date-pill--dim">
                                {t("common.notSent")}
                            </span>
                        )}
                        {invoice.sentDate && invoice.dueDate && (
                            <span className="invoice-date-pill">
                                {new Date(invoice.dueDate).toLocaleDateString()}
                            </span>
                        )}
                    </span>
                </td>

                <td className="invoice-balance-td" onClick={(e) => e.stopPropagation()}>
                    {overpaid > 0 ? (
                        <span
                            className="patient-pill patient-pill--overpaid"
                            data-tooltip={t("invoicePage.overpaidBy", { amount: overpaid.toFixed(2) })}
                        >
                            {"+€ "}{overpaid.toFixed(2)}
                        </span>
                    ) : isPaid ? (
                        <span className="patient-pill patient-pill--paid">
                            {"€ "}{remaining.toFixed(2)}
                        </span>
                    ) : remaining > 0 ? (
                        <span className="patient-pill patient-pill--open">
                            {"€ "}{remaining.toFixed(2)}
                            {vatUniform && (
                                <span className="invoice-row__vat-badge" data-tooltip={t("invoicePage.vatIncluded", { rate: vatBreakdown[0].rate })}>
                                    {t("invoicePage.vatBadge", { rate: vatBreakdown[0].rate })}
                                </span>
                            )}
                            {!vatUniform && vatAmount > 0 && (
                                <span className="invoice-row__vat-badge" data-tooltip={t("invoicePage.vatIncludedMixed")}>
                                    {t("invoicePage.vatBadgeMixed")}
                                </span>
                            )}
                            {(invoice.dunningFineAmount ?? 0) > 0 && (
                                <span
                                    className="invoice-row__fine"
                                    data-tooltip={t("invoicePage.dunningFineTooltip", {
                                        amount: invoice.dunningFineAmount!.toFixed(2),
                                        date: invoice.dueDate
                                            ? new Date(invoice.dueDate).toLocaleDateString()
                                            : "—",
                                    })}
                                >
                                    {" +€ "}{invoice.dunningFineAmount!.toFixed(2)} ℹ
                                </span>
                            )}
                        </span>
                    ) : (
                        <span className="patient-pill patient-pill--zero">{"—"}</span>
                    )}
                </td>

                {showTags && (
                    <td className="invoice-tag-td">
                        {invoice.tagIds?.length ? (
                            <div className="invoice-tag-td__chips">
                                {invoice.tagIds.map((tid) => tagMap?.get(tid)).filter(Boolean).map((tag) => (
                                    <span
                                        key={tag!.id}
                                        className="invoice-tag-chip"
                                        style={getTagStyle(tag!.name) as CSSProperties}
                                    >
                                        {tag!.name}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <span className="patient-pill patient-pill--zero">{"—"}</span>
                        )}
                    </td>
                )}

                <td
                    className="row-actions-cell"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="row-actions">
                    <button
                        title={t("invoiceRow.openInvoice")}
                        onClick={() => navigate(`/invoices/${invoice.id}`)}
                    >
                        <FaRegFolderOpen />
                    </button>
                    {(invoice.cardPaidAmount ?? 0) > 0 && (
                        <button
                            className="success"
                            title={t("invoiceRow.paidByCard", { amount: invoice.cardPaidAmount!.toFixed(2) })}
                            onClick={() => navigate(`/invoices/${invoice.id}`)}
                        >
                            <TbCreditCard />
                        </button>
                    )}
                    {onCardPaid && (
                        <CardPaymentButton
                            variant="row"
                            className="success"
                            invoiceId={invoice.id}
                            readableId={invoice.readableId}
                            qr={!!invoice.cardDeskQr}
                            terminal={!!invoice.cardTerminal}
                            onPaid={onCardPaid}
                        />
                    )}
                    {emailEnabled && (
                        <button
                            className="info"
                            title={
                                postOnly
                                    ? t("invoicePage.postOnlyBlocked")
                                    : legalBlocked
                                    ? t("invoicePage.legalNotAccepted")
                                    : !invoice.recipient?.email
                                      ? t("invoiceRow.noEmail")
                                      : t(alreadySent ? "invoiceRow.resendPortalLink" : "invoiceRow.sendPortalLink")
                            }
                            disabled={invoice.status === IS.DELETED}
                            onClick={() => {
                                if (postOnly || legalBlocked) {
                                    setPopup({
                                        title: t("invoiceRow.cannotSendYet"),
                                        message: t(postOnly ? "invoicePage.postOnlyBlocked" : "invoicePage.legalNotAccepted"),
                                        action: () => navigate(`/patients/${invoice.recipient?.id}`),
                                        confirmLabel: t("invoiceRow.goToRecipient"),
                                        confirmVariant: "primary",
                                    });
                                    return;
                                }
                                if (!invoice.recipient?.email) {
                                    setPopup({
                                        title: t("invoiceRow.cannotSendYet"),
                                        message: t("invoiceRow.noEmail"),
                                        action: () => navigate(`/patients/${invoice.recipient?.id}`),
                                        confirmLabel: t("invoiceRow.goToRecipient"),
                                        confirmVariant: "primary",
                                    });
                                    return;
                                }
                                setPopup({
                                    title: t(
                                        alreadySent
                                            ? "invoicePage.resendLink"
                                            : "invoicePage.sendLink",
                                    ),
                                    message: (
                                        <div className="send-link-popup">
                                            <p>{t(alreadySent ? "invoiceRow.resendLinkMsg" : "invoiceRow.sendLinkMsg", { id: invoice.readableId })}</p>
                                            <div className="send-link-popup__email">
                                                <MdOutlineMail />
                                                <strong>{invoice.recipient?.email}</strong>
                                            </div>
                                            {!!invoice.recipient?.tel?.length && (
                                                <div className="send-link-popup__email">
                                                    <MdPhone />
                                                    <strong>{invoice.recipient.tel[0]}</strong>
                                                </div>
                                            )}
                                        </div>
                                    ),
                                    action: () =>
                                        onSend?.(invoice.id, invoice.readableId),
                                    confirmLabel: t("recipients.send"),
                                    confirmVariant: "primary",
                                });
                            }}
                        >
                            {alreadySent ? <TbRefresh /> : <TbLink />}
                        </button>
                    )}
                    {postEnabled && (
                        <button
                            className="info"
                            title={
                                invoice.letterXpressId
                                    ? "Check letter status"
                                    : postBlocked
                                      ? t("invoiceRow.postConsentRequired")
                                      : !invoice.recipient?.street
                                        ? t("invoiceRow.noAddress")
                                        : t("invoiceRow.sendViaPost")
                            }
                            disabled={invoice.status === IS.DELETED}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (invoice.letterXpressId) {
                                    onCheckLetterStatus(invoice.letterXpressId);
                                    return;
                                }
                                if (postBlocked) {
                                    setPopup({
                                        title: t("invoiceRow.cannotSendYet"),
                                        message: t("invoiceRow.postConsentRequired"),
                                        action: () => navigate(`/patients/${invoice.recipient?.id}`),
                                        confirmLabel: t("invoiceRow.goToRecipient"),
                                        confirmVariant: "primary",
                                    });
                                    return;
                                }
                                if (!invoice.recipient?.street) {
                                    setPopup({
                                        title: t("invoiceRow.cannotSendYet"),
                                        message: t("invoiceRow.noAddress"),
                                        action: () => navigate(`/patients/${invoice.recipient?.id}`),
                                        confirmLabel: t("invoiceRow.goToRecipient"),
                                        confirmVariant: "primary",
                                    });
                                    return;
                                }
                                onSendPost?.(invoice.id);
                            }}
                        >
                            {invoice.letterXpressId ? (
                                <FiTruck />
                            ) : (
                                <SlEnvolopeLetter />
                            )}
                        </button>
                    )}
                    {canDelete && (() => {
                        const isSent = !!(invoice.sentDate);
                        const isPaid = invoice.status === IS.PAID;
                        const isOverdue = invoice.status === IS.OVERDUE;
                        const blockedReason = isSent
                            ? t("invoiceRow.cannotDeleteSent")
                            : isPaid
                              ? t("invoiceRow.cannotDeletePaid")
                              : isOverdue
                                ? t("invoiceRow.cannotDeleteOverdue")
                                : null;
                        return (
                            <button
                                className="danger"
                                title={blockedReason ?? t("invoiceRow.deleteInvoice")}
                                disabled={!!blockedReason}
                                onClick={() =>
                                    setPopup({
                                        title: t("invoices.delete"),
                                        message: t("invoiceRow.deleteMsg", {
                                            id: invoice.readableId,
                                        }),
                                        action: () => onDelete(invoice.id),
                                        confirmLabel: t("popup.ok"),
                                        confirmVariant: "danger",
                                    })
                                }
                            >
                                <RiDeleteBin2Line />
                            </button>
                        );
                    })()}
                    {showPaywise && invoice.dunning === 4 && !isPaid && (
                        invoice.paywiseCaseId ? (
                            <button
                                className="success row-actions__paywise-done"
                                title={t("settings.paywiseAlreadySubmitted")}
                                disabled
                            >
                                <TbGavel />
                            </button>
                        ) : (
                            <button
                                className="info"
                                title={t("settings.paywiseSubmitBtn")}
                                onClick={() => onPaywise?.(invoice.id)}
                            >
                                <TbGavel />
                            </button>
                        )
                    )}
                    </div>
                </td>
            </tr>
        </>
    );
});

export default InvoiceRow;
